import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { Redirect } from "wouter";

// Mirror of FeatureDef from @workspace/db/feature-registry. Kept loose to
// avoid forcing the store SPA to import the full db package (which pulls in
// node-postgres and crashes Vite). The server returns the canonical list.
export interface FeatureDefDTO {
  slug: string;
  nameAr: string;
  nameEn: string;
  group: string;
  defaultPackages: string[];
}

export interface EntitlementsState {
  loaded: boolean;
  tenantId: number | null;
  packageId: number | null;
  packageCode: string | null;
  packageNameAr: string | null;
  packageNameEn: string | null;
  features: string[];
  catalog: FeatureDefDTO[];
  refetch: () => void;
}

const DEFAULT_STATE: EntitlementsState = {
  loaded: false,
  tenantId: null,
  packageId: null,
  packageCode: null,
  packageNameAr: null,
  packageNameEn: null,
  features: [],
  catalog: [],
  refetch: () => {},
};

const Ctx = createContext<EntitlementsState>(DEFAULT_STATE);

// Legacy alias: old packages stored `hide_platform_badge` instead of
// `white_label`. Mirror the server's `FEATURE_ALIASES` so the frontend
// answers correctly for either spelling.
const ALIASES: Record<string, string> = {
  hide_platform_badge: "white_label",
};
const normalize = (slug: string): string => ALIASES[slug] ?? slug;

export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<EntitlementsState>(DEFAULT_STATE);

  const load = useCallback(() => {
    const base = (import.meta as any).env?.BASE_URL || "/";
    fetch(`${base}api/me/entitlements`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: any) => {
        if (!d) return;
        setState({
          loaded: true,
          tenantId: d.tenantId ?? null,
          packageId: d.packageId ?? null,
          packageCode: d.packageCode ?? null,
          packageNameAr: d.packageNameAr ?? null,
          packageNameEn: d.packageNameEn ?? null,
          features: Array.isArray(d.features) ? d.features : [],
          catalog: Array.isArray(d.catalog) ? d.catalog : [],
          refetch: load,
        });
      })
      .catch(() => {
        // On failure, mark loaded so consumers don't hang forever; treat as
        // "no entitlements" which fails-OPEN at the UI layer (the API
        // layer remains authoritative).
        setState((s) => ({ ...s, loaded: true, refetch: load }));
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useEntitlements(): EntitlementsState {
  return useContext(Ctx);
}

// Returns `true` if the tenant has the feature. Fails-OPEN before load
// completes so the UI doesn't flicker locked → unlocked on every nav. The
// API remains the source of truth.
export function useHasFeature(slug: string | undefined): boolean {
  const ent = useContext(Ctx);
  if (!slug) return true;
  if (!ent.loaded) return true;
  if (!ent.tenantId) return true; // platform host, no tenant context
  if (ent.packageId === null) return true; // tenant has no package assigned → no gating yet
  const target = normalize(slug);
  return ent.features.some((f) => normalize(f) === target);
}

// Map admin sidebar hrefs → feature slug. Items not in this map are
// considered "core" and always visible.
export const HREF_TO_FEATURE: Record<string, string> = {
  // Catalog
  "/admin/brands": "brands",
  "/admin/product-options": "product_options",
  "/admin/bundles": "bundles",
  "/admin/bundle-offers": "bundles",
  "/admin/grouped-products": "grouped_products",
  "/admin/auctions": "auctions",
  "/admin/warehouses": "multi_warehouse",
  // Sales
  "/admin/pos": "pos",
  "/admin/returns": "returns",
  "/admin/return-config": "returns",
  "/admin/gift-vouchers": "gift_vouchers",
  "/admin/subscriptions": "subscriptions",
  "/admin/b2b": "b2b",
  "/admin/product-qa": "product_qa",
  // Marketing
  "/admin/abandoned-carts": "abandoned_carts",
  "/admin/flash-sales": "flash_sales",
  "/admin/affiliates": "affiliates",
  "/admin/loyalty": "loyalty",
  "/admin/email-campaigns": "email_campaigns",
  "/admin/sms-campaigns": "sms_campaigns",
  "/admin/marketing-campaigns": "marketing_campaigns",
  "/admin/campaign-roi": "marketing_campaigns",
  // Communication
  "/admin/whatsapp": "whatsapp",
  "/admin/whatsapp-templates": "whatsapp",
  "/admin/site-chat": "site_chatbot",
  "/admin/newsletter": "newsletter",
  // AI
  "/admin/ai-search": "ai_search",
  "/admin/ai-studio": "ai_studio",
  "/admin/auto-reviews": "ai_auto_reviews",
  // Content
  "/admin/blog-posts": "blog",
  "/admin/blog-categories": "blog",
  "/admin/blog-comments": "blog",
  "/admin/blog-settings": "blog",
  // Operations
  "/admin/accounting": "accounting",
  "/admin/bi": "bi",
  "/admin/anti-fraud": "anti_fraud",
  "/admin/campaign-roi-reports": "advanced_reports",
  "/admin/import-export": "import_export",
  // Branding
  "/admin/nexus-store": "nexus_store_channel",
  "/admin/custom-domain": "custom_domain",
  "/admin/custom-css": "custom_css",
  "/admin/fonts": "custom_fonts",
};

export function featureForHref(href: string): string | undefined {
  return HREF_TO_FEATURE[href];
}

// Route guard. Wrap an admin page so direct URL access to a locked feature
// (sidebar already filters it) redirects to the upgrade page. Fails-OPEN
// while entitlements are still loading to avoid a flash redirect on first
// paint; the API remains authoritative if a request actually fires.
export function RequireFeature({ slug, children }: { slug: string; children: ReactNode }) {
  const ent = useContext(Ctx);
  if (!ent.loaded) return <>{children}</>;
  if (!ent.tenantId) return <>{children}</>;
  if (ent.packageId === null) return <>{children}</>;
  const target = normalize(slug);
  const has = ent.features.some((f) => normalize(f) === target);
  if (has) return <>{children}</>;
  return <Redirect to={`/admin/upgrade?feature=${encodeURIComponent(slug)}`} />;
}
